import React, { useState, useEffect } from "react";
import {
  Card,
  Row,
  Col,
  Table,
  Typography,
  Button,
  Modal,
  Form,
  Input,
  Space,
  Popconfirm,
  message,
  Spin,
  Statistic,
  Avatar,
  Badge,
  Tooltip,
} from "antd";
import {
  UserOutlined,
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  InfoCircleOutlined,
  TeamOutlined,
  UserAddOutlined,
  MailOutlined,
  LockOutlined,
} from "@ant-design/icons";
import api from "../api/Api";

const { Title, Text } = Typography;

const GestaoUsuarios = () => {
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form] = Form.useForm();

  const fetchUsuarios = async () => {
    setLoading(true);
    try {
      const response = await api.ListUser();
      if (Array.isArray(response)) setUsuarios(response);
    } catch (error) {
      console.error("Erro ao listar usuários:", error);
      message.error("Erro ao carregar usuários");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsuarios();
  }, []);

  const handleSalvar = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      if (editingUser) {
        await api.AlterUser({ ...values, id: editingUser.id });
        message.success("Usuário atualizado com sucesso!");
      } else {
        await api.AddUser(values);
        message.success("Usuário adicionado com sucesso!");
      }

      setModalVisible(false);
      setEditingUser(null);
      form.resetFields();
      fetchUsuarios();
    } catch (error) {
      console.error("Erro ao salvar:", error);
      message.error("Erro ao salvar usuário");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      setLoading(true);
      await api.DeleteUser({ id });
      message.success("Usuário eliminado com sucesso!");
      fetchUsuarios();
    } catch (error) {
      console.error("Erro ao eliminar usuário:", error);
      message.error("Erro ao eliminar usuário");
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: "Usuário",
      dataIndex: "nome",
      key: "nome",
      render: (text, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <Avatar
            style={{ backgroundColor: "#1890ff", verticalAlign: "middle" }}
            icon={<UserOutlined />}
          />
          <div>
            <Text strong style={{ fontSize: "16px" }}>{text}</Text>
            <br />
            <Text type="secondary" style={{ fontSize: "13px" }}>{record.email}</Text>
          </div>
        </div>
      ),
    },
    {
      title: "Perfil",
      dataIndex: "perfil",
      key: "perfil",
      align: "center",
      render: (perfil) => (
        <Badge
          color={perfil === "admin" ? "#faad14" : "#1890ff"}
          text={
            <Text style={{ textTransform: "capitalize" }}>{perfil || "Usuário"}</Text>
          }
        />
      ),
    },
    {
      title: "Ações",
      key: "acoes",
      align: "center",
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="Editar usuário">
            <Button
              icon={<EditOutlined />}
              type="text"
              shape="circle"
              onClick={() => {
                setEditingUser(record);
                form.setFieldsValue(record);
                setModalVisible(true);
              }}
            />
          </Tooltip>
          <Popconfirm
            title="Eliminar usuário?"
            okText="Sim"
            cancelText="Não"
            onConfirm={() => handleDelete(record.id)}
          >
            <Tooltip title="Eliminar usuário">
              <Button icon={<DeleteOutlined />} type="text" danger shape="circle" />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(145deg, #f0f2f5 0%, #f8f9fa 100%)",
        padding: "32px 48px",
      }}
    >
      {/* Cabeçalho */}
      <div
        style={{
          marginBottom: "32px",
          padding: "24px 32px",
          background: "linear-gradient(135deg, rgba(24,144,255,0.08) 0%, rgba(24,144,255,0.02) 100%)",
          borderRadius: "20px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.03)",
          border: "1px solid rgba(24,144,255,0.1)",
        }}
      >
        <Row align="middle" justify="space-between">
          <Col>
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <Avatar
                size={64}
                icon={<TeamOutlined />}
                style={{ backgroundColor: "#1890ff", boxShadow: "0 4px 12px rgba(24,144,255,0.3)" }}
              />
              <div>
                <Title level={2} style={{ margin: 0, color: "#1a3353" }}>
                  Gestão de Usuários
                </Title>
                <Text type="secondary" style={{ fontSize: "16px" }}>
                  Gerencie contas de acesso e permissões da plataforma
                </Text>
              </div>
            </div>
          </Col>
          <Col>
            <Button
              type="primary"
              size="large"
              icon={<PlusOutlined />}
              onClick={() => {
                form.resetFields();
                setEditingUser(null);
                setModalVisible(true);
              }}
              style={{
                borderRadius: "10px",
                boxShadow: "0 4px 12px rgba(24,144,255,0.3)",
                height: "48px",
                padding: "0 24px",
                fontWeight: 500,
              }}
            >
              Adicionar Usuário
            </Button>
          </Col>
        </Row>
      </div>

      {/* Tabela */}
      <Card
        bordered={false}
        style={{
          borderRadius: "20px",
          boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
          overflow: "hidden",
        }}
        bodyStyle={{ padding: 0 }}
      >
        {loading ? (
          <div style={{ textAlign: "center", padding: "80px 20px" }}>
            <Spin size="large" />
            <Text style={{ display: "block", marginTop: "20px", color: "#666" }}>
              Carregando usuários...
            </Text>
          </div>
        ) : usuarios.length > 0 ? (
          <Table
            rowKey="id"
            columns={columns}
            dataSource={usuarios}
            pagination={{
              pageSize: 5,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} de ${total} usuários`,
              position: ["bottomCenter"],
              style: { margin: "16px 0" },
            }}
            rowClassName={() => "custom-row-hover"}
            style={{ background: "transparent" }}
          />
        ) : (
          <div style={{ textAlign: "center", padding: "80px 20px", background: "#fafafa" }}>
            <InfoCircleOutlined style={{ fontSize: "64px", color: "#1890ff", opacity: 0.6 }} />
            <Title level={4} style={{ color: "#666", marginTop: "20px" }}>
              Nenhum usuário encontrado
            </Title>
            <Text type="secondary" style={{ fontSize: "16px" }}>
              Clique no botão "Adicionar Usuário" para começar a cadastrar.
            </Text>
          </div>
        )}
      </Card>

      {/* Modal */}
      <Modal
        className="modal-usuarios"
        open={modalVisible}
        title={
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {editingUser ? <EditOutlined /> : <PlusOutlined />}
            <span>{editingUser ? "Editar Usuário" : "Adicionar Novo Usuário"}</span>
          </div>
        }
        onCancel={() => {
          setModalVisible(false);
          setEditingUser(null);
          form.resetFields();
        }}
        onOk={handleSalvar}
        okText={editingUser ? "Salvar Alterações" : "Adicionar"}
        cancelText="Cancelar"
        centered
        width={500}
        okButtonProps={{
          style: {
            borderRadius: "8px",
            boxShadow: editingUser ? "none" : "0 2px 8px rgba(24,144,255,0.3)",
          },
        }}
        cancelButtonProps={{ style: { borderRadius: "8px" } }}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ nome: "", email: "", password: "" }}
        >
          <Form.Item
            label="Nome completo"
            name="nome"
            rules={[{ required: true, message: "Informe o nome do usuário" }]}
          >
            <Input
              prefix={<UserOutlined style={{ color: "#bfbfbf" }} />}
              placeholder="Ex: João Silva"
              size="large"
              style={{ borderRadius: "10px" }}
            />
          </Form.Item>

          <Form.Item
            label="E-mail"
            name="email"
            rules={[
              { required: true, message: "Informe o e-mail" },
              { type: "email", message: "E-mail inválido" },
            ]}
          >
            <Input
              prefix={<MailOutlined style={{ color: "#bfbfbf" }} />}
              placeholder="exemplo@dominio.com"
              size="large"
              style={{ borderRadius: "10px" }}
            />
          </Form.Item>

          <Form.Item
            label="Senha"
            name="password"
            rules={[{ required: true, message: "Informe a senha" }]}
            tooltip="Mínimo de 6 caracteres"
          >
            <Input.Password
              prefix={<LockOutlined style={{ color: "#bfbfbf" }} />}
              placeholder="••••••"
              size="large"
              style={{ borderRadius: "10px" }}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Estilos locais — isolados pela classe .modal-usuarios */}
      <style>{`
        .custom-row-hover:hover {
          background-color: #f0f7ff !important;
          transition: background-color 0.2s;
        }

        /* Força texto preto nos inputs do modal de usuários */
        .modal-usuarios .ant-input,
        .modal-usuarios .ant-input-affix-wrapper,
        .modal-usuarios .ant-input-affix-wrapper input {
          color: #000000 !important;
          background-color: #ffffff !important;
          -webkit-text-fill-color: #000000 !important;
        }

        /* Corrige autocomplete do browser (fundo amarelo / texto invisível) */
        .modal-usuarios .ant-input:-webkit-autofill,
        .modal-usuarios .ant-input-affix-wrapper input:-webkit-autofill {
          -webkit-text-fill-color: #000000 !important;
          -webkit-box-shadow: 0 0 0px 1000px #ffffff inset !important;
        }

        /* Placeholder visível */
        .modal-usuarios .ant-input::placeholder,
        .modal-usuarios .ant-input-affix-wrapper input::placeholder {
          color: #bfbfbf !important;
          -webkit-text-fill-color: #bfbfbf !important;
        }
      `}</style>
    </div>
  );
};

export default GestaoUsuarios;